import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings, Check } from 'lucide-react';

const CONSENT_KEY = 'eldera_cookie_consent';
const CONSENT_VERSION = '2025-01-01';

// Check if we're in production (eldera.lv domain)
const isProduction = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'eldera.lv' || hostname === 'www.eldera.lv';
};

// Cookie consent types
const COOKIE_TYPES = {
  necessary: {
    id: 'necessary',
    label: 'Nepieciešamās sīkdatnes',
    description: 'Šīs sīkdatnes ir nepieciešamas vietnes darbībai. Tās nevar atspējot.',
    required: true,
    defaultEnabled: true
  },
  analytics: {
    id: 'analytics',
    label: 'Analītikas sīkdatnes',
    description: 'Šīs sīkdatnes palīdz mums saprast, kā apmeklētāji izmanto vietni, lai to uzlabotu.',
    required: false,
    defaultEnabled: false
  }
};

const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({
    necessary: true,
    analytics: false,
    version: CONSENT_VERSION,
    timestamp: null
  });

  // Load existing consent on mount
  useEffect(() => {
    const savedConsent = localStorage.getItem(CONSENT_KEY);
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        // Check if consent version matches
        if (parsed.version === CONSENT_VERSION) {
          setConsent(parsed);
          // Apply saved consent
          applyConsent(parsed);
        } else {
          // Version mismatch, show banner again
          setShowBanner(true);
        }
      } catch (e) {
        setShowBanner(true);
      }
    } else {
      // No saved consent, show banner
      setShowBanner(true);
      // Block analytics by default until consent
      blockAnalytics();
    }
  }, []);

  // Block PostHog and other analytics
  const blockAnalytics = () => {
    if (typeof window !== 'undefined') {
      // Block PostHog
      if (window.posthog) {
        window.posthog.opt_out_capturing();
      }
      // Set flag for other scripts
      window.__eldera_analytics_blocked = true;
    }
  };

  // Enable analytics
  const enableAnalytics = () => {
    if (typeof window !== 'undefined') {
      // Enable PostHog
      if (window.posthog) {
        window.posthog.opt_in_capturing();
      }
      window.__eldera_analytics_blocked = false;
    }
  };

  // Apply consent settings
  const applyConsent = (consentSettings) => {
    if (consentSettings.analytics) {
      enableAnalytics();
    } else {
      blockAnalytics();
    }
  };

  // Save consent to localStorage and apply
  const saveConsent = (newConsent) => {
    const consentData = {
      ...newConsent,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    setConsent(consentData);
    applyConsent(consentData);
    setShowBanner(false);
    setShowSettings(false);
  };

  // Accept all cookies
  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true
    });
  };

  // Reject optional cookies
  const rejectOptional = () => {
    saveConsent({
      necessary: true,
      analytics: false
    });
  };

  // Save custom settings
  const saveSettings = () => {
    saveConsent(consent);
  };

  // Toggle individual cookie type
  const toggleCookie = (type) => {
    if (COOKIE_TYPES[type].required) return;
    setConsent(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Open settings from footer link
  useEffect(() => {
    const handleOpenSettings = () => {
      setShowBanner(true);
      setShowSettings(true);
    };
    window.openCookieSettings = handleOpenSettings;
    return () => {
      delete window.openCookieSettings;
    };
  }, []);

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" onClick={() => {}} />
      
      {/* Banner */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-[#E6ECE8] shadow-2xl"
        role="dialog"
        aria-label="Sīkdatņu iestatījumi"
        data-testid="cookie-banner"
      >
        <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
          {!showSettings ? (
            /* Main Banner */
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie className="text-[#8FD16A] flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-[#1A3A5C] mb-1">
                    Mēs izmantojam sīkdatnes
                  </h3>
                  <p className="text-sm text-[#4A6B7A]">
                    Mēs izmantojam sīkdatnes, lai nodrošinātu vietnes darbību un uzlabotu jūsu pieredzi. 
                    Dažas sīkdatnes ir nepieciešamas, bet citas palīdz mums analizēt apmeklējumu.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                <button
                  onClick={rejectOptional}
                  className="px-4 py-2.5 text-sm font-medium text-[#1A3A5C] bg-[#F0F7E6] hover:bg-[#E6ECE8] rounded-lg transition-colors"
                  data-testid="cookie-reject"
                >
                  Noraidīt
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-4 py-2.5 text-sm font-medium text-[#1A3A5C] border border-[#E6ECE8] hover:bg-[#F0F7E6] rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="cookie-customize"
                >
                  <Settings size={16} />
                  Personalizēt
                </button>
                <button
                  onClick={acceptAll}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-[#1A3A5C] hover:bg-[#2A4A6C] rounded-lg transition-colors"
                  data-testid="cookie-accept"
                >
                  Pieņemt visas
                </button>
              </div>
            </div>
          ) : (
            /* Settings Panel */
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#1A3A5C] flex items-center gap-2">
                  <Settings size={20} />
                  Sīkdatņu iestatījumi
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-1 hover:bg-[#F0F7E6] rounded-lg transition-colors"
                  aria-label="Aizvērt"
                >
                  <X size={20} className="text-[#4A6B7A]" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                {Object.values(COOKIE_TYPES).map((type) => (
                  <div 
                    key={type.id}
                    className="flex items-start gap-4 p-4 bg-[#F0F7E6] rounded-xl"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-[#1A3A5C]">{type.label}</h4>
                        {type.required && (
                          <span className="text-xs bg-[#1A3A5C] text-white px-2 py-0.5 rounded">
                            Obligātas
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#4A6B7A]">{type.description}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={consent[type.id]}
                        onChange={() => toggleCookie(type.id)}
                        disabled={type.required}
                        className="sr-only peer"
                        data-testid={`cookie-toggle-${type.id}`}
                      />
                      <div className={`w-11 h-6 rounded-full peer 
                        ${type.required ? 'bg-[#8FD16A] cursor-not-allowed' : 'bg-gray-200 peer-checked:bg-[#8FD16A]'}
                        peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#8FD16A]/30
                        after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                        after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all
                        peer-checked:after:translate-x-full`}
                      />
                    </label>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={rejectOptional}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-[#1A3A5C] bg-[#F0F7E6] hover:bg-[#E6ECE8] rounded-lg transition-colors"
                >
                  Noraidīt visas neobligātās
                </button>
                <button
                  onClick={saveSettings}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-[#1A3A5C] hover:bg-[#2A4A6C] rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="cookie-save-settings"
                >
                  <Check size={16} />
                  Saglabāt izvēli
                </button>
              </div>
              
              <p className="text-xs text-[#4A6B7A] mt-4 text-center">
                Varat mainīt savu izvēli jebkurā laikā. Vairāk informācijas —{' '}
                <a href="/pardosanas-un-lietosanas-noteikumi#sikdatnes" className="text-[#8FD16A] hover:underline">
                  sīkdatņu politikā
                </a>.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsentBanner;
